import React, { useState, useEffect } from 'react';
import './App.css';
import DerivationTree from './DerivationTree';
import NFA from './NFA';
import NFAVisualization from './NFAVisualization';

function App() {
  const [regex, setRegex] = useState('');
  const [derivationTree, setDerivationTree] = useState(null);
  const [testInput, setTestInput] = useState('');
  const [testResults, setTestResults] = useState([]);
  const [generatorActive, setGeneratorActive] = useState(false);
  const [nfa, setNfa] = useState(null);
  const [error, setError] = useState(null);
  const [exampleVisible, setExampleVisible] = useState(false);

  // Przykładowe wyrażenia regularne do szybkiego wyboru
  const exampleRegexes = [
    { label: 'Proste: a', value: 'a' },
    { label: 'Alternatywa: a|b', value: 'a|b' },
    { label: 'Złożone: {ab|c}', value: '{ab|c}' },
    { label: 'Z opcją: a[b]c', value: 'a[b]c' },
    { label: 'Plus: a{a}', value: 'a{a}' }
  ];

  // Efekt do przewijania do wyników testów
  useEffect(() => {
    if (testResults.length > 0) {
      const resultsElement = document.querySelector('.results');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [testResults]);

  const handleRegexSubmit = (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      // Parsowanie wyrażenia regularnego i generowanie drzewa wyprowadzeń
      const parser = new RegexParser(regex);
      const tree = parser.parse();
      setDerivationTree(tree);
      
      // Generowanie NFA z drzewa wyprowadzeń
      const generatedNfa = NFA.fromDerivationTree(tree);
      setNfa(generatedNfa);
      
      setGeneratorActive(true);
      setTestResults([]);
    } catch (error) {
      console.error('Error:', error);
      setError(`Błąd: ${error.message}`);
    }
  };

  const handleTestInput = (e) => {
    e.preventDefault();
    
    if (!generatorActive || !nfa) {
      setError('Najpierw wygeneruj automat na podstawie wyrażenia regularnego.');
      return;
    }

    if (testInput.trim() === '') {
      // Specjalne traktowanie pustego słowa dla lepszej czytelności UI
      const accepted = nfa.test('');
      setTestResults([{ input: '', accepted, timestamp: Date.now() }, ...testResults]);
      return;
    }

    // Sprawdzanie, czy wejście jest akceptowane przez NFA
    const accepted = nfa.test(testInput);
    
    // Dodaj nowy wynik na początek listy zamiast na koniec
    setTestResults([{ input: testInput, accepted, timestamp: Date.now() }, ...testResults]);
    setTestInput('');
  };

  const handleClearHistory = () => {
    setTestResults([]);
  };

  const handleQuit = () => {
    setTestInput('');
    setGeneratorActive(false);
    setDerivationTree(null);
    setTestResults([]);
    setRegex('');
    setNfa(null);
    setError(null);
  };

  const selectExampleRegex = (example) => {
    setRegex(example);
    setExampleVisible(false);
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Generator Automatów Skończonych (NFA)</h1>
      </header>
      <main className="App-main">
        <div className="generator-section">
          <form onSubmit={handleRegexSubmit}>
            <label>
              Wprowadź wyrażenie regularne:
              <div className="input-container">
                <input
                  type="text"
                  value={regex}
                  onChange={(e) => setRegex(e.target.value)}
                  placeholder="np. {ab|c}"
                />
                <button 
                  type="button" 
                  className="examples-button"
                  onClick={() => setExampleVisible(!exampleVisible)}
                  title="Pokaż przykłady"
                >
                  ?
                </button>
              </div>
              {exampleVisible && (
                <div className="examples-dropdown">
                  <div className="examples-header">
                    <h4>Przykładowe wyrażenia</h4>
                    <button 
                      type="button" 
                      className="close-button"
                      onClick={() => setExampleVisible(false)}
                    >
                      ×
                    </button>
                  </div>
                  <ul>
                    {exampleRegexes.map((example, index) => (
                      <li key={index} onClick={() => selectExampleRegex(example.value)}>
                        {example.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </label>
            <div className="syntax-help">
              <strong>Składnia:</strong>
              <ul>
                <li>{"{a}"} - domknięcie Kleene'ego (0 lub więcej wystąpień)</li>
                <li>a{"{a}"} - plus (1 lub więcej wystąpień)</li>
                <li>{"[a]"} - opcjonalnie (0 lub 1 wystąpienie)</li>
                <li>(a) - grupowanie</li>
                <li>a|b - alternatywa</li>
              </ul>
            </div>
            <button type="submit" className="generate-button">
              <span className="button-icon">&#x2699;</span> Generuj Automat
            </button>
          </form>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>
        
        {derivationTree && (
          <div className="derivation-tree">
            <h2>Drzewo wyprowadzeń:</h2>
            <div className="tree-container">
              <DerivationTree tree={derivationTree} />
            </div>
          </div>
        )}
        
        {nfa && (
          <div className="nfa-container">
            <NFAVisualization nfa={nfa} />
          </div>
        )}
        
        {generatorActive && nfa && (
          <div className="test-section">
            <h2>Testowanie automatu:</h2>
            <form onSubmit={handleTestInput}>
              <label>
                Wprowadź słowo do przetestowania:
                <div className="input-container">
                  <input
                    type="text"
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                    placeholder="np. ab (dla pustego słowa zostaw puste)"
                  />
                  <button 
                    type="button" 
                    className="clear-input" 
                    onClick={() => setTestInput('')}
                    title="Wyczyść pole"
                  >
                    ×
                  </button>
                </div>
              </label>
              <div className="button-container">
                <button type="submit" className="test-button">
                  <span className="button-icon">&#x1F50D;</span> Sprawdź
                </button>
                <button type="button" onClick={handleQuit} className="quit-button">
                  <span className="button-icon">&#x21BB;</span> Resetuj
                </button>
              </div>
            </form>
            
            {testResults.length > 0 && (
              <div className="results">
                <div className="results-header">
                  <h3>Wyniki testów:</h3>
                  <button 
                    type="button" 
                    className="clear-history" 
                    onClick={handleClearHistory}
                    title="Wyczyść historię"
                  >
                    Wyczyść historię
                  </button>
                </div>
                <ul className="results-list">
                  {testResults.map((result, index) => (
                    <li key={result.timestamp || index} className={`result-item ${result.accepted ? 'accepted' : 'rejected'}`}>
                      <div className="result-content">
                        <span className="result-word">Słowo '{result.input === '' ? "ε (słowo puste)" : result.input}'</span> 
                        <span className={`result-status ${result.accepted ? 'status-accepted' : 'status-rejected'}`}>
                          {result.accepted ? 'AKCEPTOWANE!' : 'NIEAKCEPTOWANE!'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </main>
      <footer className="App-footer">
        <p>Generator NFA - Implementacja na potrzeby projektu na zajęcia z Kompilatorów</p>
        <p className="footer-credits">Uniwersytet Kardynała Stefana Wyszyńskiego, 2024/2025</p>
      </footer>
    </div>
  );
}

// Klasa do parsowania wyrażeń regularnych
export class RegexParser {
  constructor(regex) {
    this.regex = regex;
    this.pos = 0;
  }

  parse() {
    const result = this.parseRegular();
    
    // Sprawdź, czy całe wyrażenie zostało przeanalizowane
    if (this.pos < this.regex.length) {
      throw new Error(`Nieoczekiwany znak '${this.regex[this.pos]}' na pozycji ${this.pos}`);
    }
    
    return result;
  }

  parseRegular() {
    return { type: 'regular', children: [this.parseAlternative()] };
  }

  parseAlternative() {
    const node = { type: 'alternative', children: [] };
    
    node.children.push(this.parseSequence());
    
    if (this.pos < this.regex.length && this.regex[this.pos] === '|') {
      this.pos++; // Pomiń '|'
      node.children.push({ type: 'PIPE', value: '|' });
      node.children.push(this.parseSequence());
    }
    
    return node;
  }

  parseSequence() {
    const node = { type: 'sequence', children: [] };
    
    node.children.push(this.parseElement());
    
    while (
      this.pos < this.regex.length && 
      this.regex[this.pos] !== ')' && 
      this.regex[this.pos] !== '}' && 
      this.regex[this.pos] !== ']' && 
      this.regex[this.pos] !== '|'
    ) {
      node.children.push(this.parseElement());
    }
    
    return node;
  }

  parseElement() {
    let node = { type: 'element', children: [] };
    
    if (this.pos < this.regex.length) {
      if (this.regex[this.pos] === '(') {
        this.pos++; // Pomiń '('
        node.children.push({ type: 'LCBRA', value: '(' });
        node.children.push(this.parseRegular());
        
        if (this.pos < this.regex.length && this.regex[this.pos] === ')') {
          this.pos++; // Pomiń ')'
          node.children.push({ type: 'RCBRA', value: ')' });
        } else {
          throw new Error('Oczekiwano nawiasu zamykającego ")"');
        }
      } else if (this.regex[this.pos] === '{') {
        this.pos++; // Pomiń '{'
        node.children.push({ type: 'LCURLY', value: '{' });
        node.children.push(this.parseRegular());
        
        if (this.pos < this.regex.length && this.regex[this.pos] === '}') {
          this.pos++; // Pomiń '}'
          node.children.push({ type: 'RCURLY', value: '}' });
        } else {
          throw new Error('Oczekiwano nawiasu zamykającego "}"');
        }
      } else if (this.regex[this.pos] === '[') {
        this.pos++; // Pomiń '['
        node.children.push({ type: 'LSQBRA', value: '[' });
        node.children.push(this.parseRegular());
        
        if (this.pos < this.regex.length && this.regex[this.pos] === ']') {
          this.pos++; // Pomiń ']'
          node.children.push({ type: 'RSQBRA', value: ']' });
        } else {
          throw new Error('Oczekiwano nawiasu zamykającego "]"');
        }
      } else {
        // Jest to symbol
        node = { type: 'element', children: [{ type: 'SYMBOL', value: this.regex[this.pos] }] };
        this.pos++;
      }
    }
    
    return node;
  }
}

export default App;
